<template>
    <div class="abstract-search-con">

        <div class="flex-con is-stretch" ref="top">
            <div class="sidebar-col">

                <div v-if="showExport" class="download-con">
                    <a
                        :href="$srcOverwrite(dealerFeedFile.feedLink)"
                        target="_blank"
                        class="button is-primary is-outlined is-block-button"
                        :disabled="loadingFeedFile"
                    >
                        Download Product Feed
                    </a>
                </div>

                <loading v-if="loadingCategories"/>
                <div v-else class="cat-col">
                    <div
                        v-for="cat in categories"
                        :key="cat.id"
                        class="parent-cat"
                        :class="{ 'selected' : categorySearch[cat.id] !== undefined }"
                    >
                        <div class="flex-con is-grow xs-pad-lr">
                            <div class="is-narrow toggle-con" @click="toggleChildren(cat)">
                                <span class="icon">
                                    <i class="fa fa-angle-down"></i>
                                </span>
                            </div>
                            <div class="parent-name" @click="searchCategory(cat)">
                                <span v-html="cat.name"/>
                            </div>
                        </div>
                        <div v-if="cat.showChildren" class="child-cats">
                            <div class="border-wall">
                                <div
                                    v-for="child in cat.children"
                                    :key="child.id"
                                    class="child-cat"
                                    :class="{ 'selected' : categorySearch[child.id] !== undefined }"
                                    @click="searchChildCategory(cat, child)"
                                >
                                    <span v-html="child.name"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="is-full">

                <div class="search-con">
                    <div class="flex-con is-grow sm-pad-lr">
                        <div class="is-narrow">
                            <div
                                class="button"
                                @click="showAdvancedSearch = !showAdvancedSearch"
                                ref="advSearchButton"
                            >
                                <span>
                                    Adv. Search
                                </span>
                                <span class="icon">
                                    <i class="fa fa-angle-down"></i>
                                </span>
                            </div>
                        </div>
                        <div>
                            <div class="field has-addons">
                                <div class="control is-expanded has-icons-left">
                                    <input
                                        class="input search-input"
                                        v-model="searchText"
                                        @keyup.enter="search()"
                                        placeholder="Search..."
                                    >
                                    <span class="icon is-small is-left">
                                    <i class="fa fa-search"></i>
                                </span>
                                </div>
                            </div>
                        </div>
                        <slot name="after-search"></slot>
                        <div class="is-narrow">
                            <pagination-controls
                                :fetching="fetching"
                                :total="data.total"
                                :items-on-page="data.items.length"
                                :limit="filters.limit"
                                :offset="filters.offset"
                                :limit-options="limitOptions"
                                @previous-page="previousPage()"
                                @next-page="nextPage()"
                                @change-page="changePage($event)"
                                @change-limit="changeLimit($event)"
                            ></pagination-controls>
                        </div>
                        <div class="is-narrow">
                            <dropdown-button @open="showAdvancedSearch = false">
                                <template #heading>
                                    <span>
                                        {{activeSortField.name}}
                                    </span>
                                </template>
                                <a
                                    v-for="(field, index) in sortFields"
                                    :key="index"
                                    @click="changeSort(field)"
                                >
                                    {{field.name}}
                                </a>
                            </dropdown-button>
                        </div>
                    </div>

                    <close-on-outside-click
                        v-if="showAdvancedSearch"
                        :element-that-opens-ref="$refs.advSearchButton"
                        class="advanced-search-con"
                        @close="showAdvancedSearch = false"
                    >

                        <div class="advanced-search-header-or-footer">
                            <h6 class="heading is-size-6 is-marginless">Advanced Search</h6>
                        </div>

                        <div class="flex-con is-grow is-equal is-start lg-pad-lr advanced-search-body">
                            <div>
                                <div
                                    v-for="field in advancedSearchIdentifierFields"
                                    :key="field.fieldHandle"
                                    class="field"
                                >
                                    <label class="label">{{field.label}}</label>
                                    <input
                                        class="input"
                                        v-model="field.value"
                                        :placeholder="field.label"
                                    />
                                </div>
                            </div>
                            <div>
                                <div
                                    v-for="field in advancedSearchMiddleColumnFields"
                                    :key="field.fieldHandle"
                                    class="field"
                                >
                                    <label class="label">{{field.label}}</label>
                                    <input
                                        class="input"
                                        v-model="field.value"
                                        :placeholder="field.label"
                                    />
                                </div>
                            </div>
                            <div>
                                <div
                                    v-for="field in advancedSearchLastColumnFields"
                                    :key="field.fieldHandle"
                                    class="field"
                                >
                                    <label class="label">{{field.label}}</label>
                                    <div class="flex-con sm-pad-lr">
                                        <div>
                                            <div class="field has-addons">
                                                <div class="control">
                                                    <div class="button is-static">
                                                        Min
                                                    </div>
                                                </div>
                                                <div class="control is-expanded">
                                                    <input
                                                        class="input"
                                                        v-model="field.min.value"
                                                        :placeholder="`${field.label} Min`"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <div class="field has-addons">
                                                <div class="control">
                                                    <div class="button is-static">
                                                        Max
                                                    </div>
                                                </div>
                                                <div class="control is-expanded">
                                                    <input
                                                        class="input"
                                                        v-model="field.max.value"
                                                        :placeholder="`${field.label} Max`"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="advanced-search-header-or-footer has-text-right">
                            <div class="button is-large m-r-md" @click="clearAdvancedSearch">
                                Clear
                            </div>
                            <div class="button is-link is-large" @click="applyAdvancedSearch">
                                Apply
                            </div>
                        </div>
                    </close-on-outside-click>

                </div>

                <div v-if="groupedAppliedFilters.length" class="filter-con">
                    <applied-filters
                        :grouped-applied-filters="groupedAppliedFilters"
                        :available-fields="availableFields"
                        @add-dynamic-filter="addDynamicFilter($event)"
                        @remove-generic-filter="removeGenericFilter($event)"
                        @remove-filter="removeFilter($event)"
                        @remove-dynamic-filter="customRemoveDynamicFilter($event)"
                        @remove-search-filter="clearSearchFilter($event)"
                    >
                    </applied-filters>
                </div>

                <loading class="m-t-xl abort" v-if="fetching"></loading>
                <div v-else-if="!data.items.length" class="no-items">
                    <slot name="no-items">
                        Nothing match your filters
                    </slot>
                </div>
                <div v-else>
                    <product-item-display
                        v-for="p in data.items"
                        :key="p.id"
                        :product="p"
                    >
                    </product-item-display>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
import ProductsApi from '../../api/Products';
import ProductFieldsUtil from '../../components/products/util/ProductFieldsUtil';

import AbstractSearchMixin from '../../components/abstract-search/AbstractSearchMixin';
import FilterMenuGroup from '../../components/abstract-search/components/FilterMenuGroup';
import FilterList from '../../components/abstract-search/filters/FilterList';

import AppliedFilters from '../../components/abstract-search/components/AppliedFilters';
import DynamicFilter from '../../components/abstract-search/components/DynamicFilter';
import PaginationControls from '../../components/abstract-search/components/PaginationControls';
import SearchSelection from '../../components/abstract-search/components/SearchSelection';
import ProductItemDisplay from "./components/ProductItemDisplay";
import AccountSettingsMixin from "../../mixins/AccountSettingsMixin";
import CloseOnOutsideClick from "../../elements/CloseOnOutsideClick";

export default {
    mixins : [AbstractSearchMixin, AccountSettingsMixin],
    components : {
        CloseOnOutsideClick,
        ProductItemDisplay,
        FilterMenuGroup,
        FilterList,
        AppliedFilters,
        DynamicFilter,
        PaginationControls,
        SearchSelection,
    },
    data () {
        return {
            //start required by mixin
            searchCacheKey : 'dealer_products',
            availableFields : ProductFieldsUtil,
            apiSearchMethod : ProductsApi.searchProducts,
            searchableFieldHandles : ProductFieldsUtil.searchableFieldHandles,
            defaultSortColumnHandle : 'product.insertedOn',
            defaultProjectionColumns : [
                'product.sku',
                'product.title',
                'product.description',
                'product.images',
                'product.minPrice',
                'product.maxPrice',
                'product.summedQuantity',
                'product.totalVariants',
                'product.categoryOne',
                'product.categoryTwo',
            ],
            //end required by mixin
            loadingFeedFile : true,
            dealerFeedFile : {},
            categories : [],
            loadingCategories : true,
            categorySearch : {},

            showAdvancedSearch : false,

            advancedSearchFields : {
                upc : {
                    value : null,
                    comparatorHandle : 'equal',
                    fieldHandle : 'variant.upc',
                    label : 'UPC',
                    identifierType : true,
                },
                mpn : {
                    value : null,
                    comparatorHandle : 'equal',
                    fieldHandle : 'variant.mpn',
                    label : 'MPN',
                    identifierType : true,
                },
                asin : {
                    value : null,
                    comparatorHandle : 'equal',
                    fieldHandle : 'variant.asin',
                    label : 'ASIN',
                    identifierType : true,
                },
                ean : {
                    value : null,
                    comparatorHandle : 'equal',
                    fieldHandle : 'variant.ean',
                    label : 'EAN',
                    identifierType : true,
                },
                brand : {
                    value : null,
                    comparatorHandle : 'match_fuzzy',
                    fieldHandle : 'product.manufacturer',
                    label : 'Brand',
                    middleColumn : true,
                },
                category1 : {
                    value : null,
                    comparatorHandle : 'match_fuzzy',
                    fieldHandle : 'product.categoryOne',
                    label : 'Category 1',
                    middleColumn : true,
                },
                category2 : {
                    value : null,
                    comparatorHandle : 'match_fuzzy',
                    fieldHandle : 'product.categoryTwo',
                    label : 'Category 2',
                    middleColumn : true,
                },
                quantity : {
                    lastColumn : true,
                    isRange : true,
                    label : 'Qty',
                    fieldHandle : 'variant.quantity',
                    min : {
                        value : null,
                        comparatorHandle : 'greater_equal',
                    },
                    max : {
                        value : null,
                        comparatorHandle : 'less_equal',
                    },
                },
                price : {
                    lastColumn : true,
                    isRange : true,
                    label : 'Price',
                    fieldHandle : 'variant.price',
                    min : {
                        value : null,
                        comparatorHandle : 'greater_equal',
                    },
                    max : {
                        value : null,
                        comparatorHandle : 'less_equal',
                    },
                },
                msrp : {
                    lastColumn : true,
                    isRange : true,
                    label : 'MSRP',
                    fieldHandle : 'variant.msrp',
                    min : {
                        value : null,
                        comparatorHandle : 'greater_equal',
                    },
                    max : {
                        value : null,
                        comparatorHandle : 'less_equal',
                    },
                },
                map : {
                    lastColumn : true,
                    isRange : true,
                    label : 'MAP',
                    fieldHandle : 'variant.map',
                    min : {
                        value : null,
                        comparatorHandle : 'greater_equal',
                    },
                    max : {
                        value : null,
                        comparatorHandle : 'less_equal',
                    },
                },
            },

            sortFields : [
                {
                    fieldHandle : 'product.insertedOn',
                    name : 'Newest',
                    desc : true,
                },
                {
                    fieldHandle : 'variant.price',
                    name : 'Price (Low to High)',
                    desc : false,
                },
                {
                    fieldHandle : 'variant.price',
                    name : 'Price (High to Low)',
                    desc : true,
                },
                {
                    fieldHandle : 'variant.quantity',
                    name : 'Quantity (Low to High)',
                    desc : false,
                },
                {
                    fieldHandle : 'variant.quantity',
                    name : 'Quantity (High to Low)',
                    desc : true,
                },
            ],

        };
    },
    beforeMount() {

        if(!this.showProducts) {
            this.$router.replace({ name : this.$ROUTES.DASHBOARD });
            return;
        }

        if(this.showExport) {
            ProductsApi.getDealerFeedFile().then(response => {
                this.dealerFeedFile = response.data;
                this.loadingFeedFile = false;
            });
        }
        ProductsApi.getCategories().then(response => {
            if(this.categorySearch) {
                Object.keys(this.categorySearch).forEach(id => {
                    const parsedId = parseInt(id);
                    if(this.categorySearch[id] === 'parent') {
                        const c = response.data.find(c => c.id === parsedId);
                        if(c) {
                            c.showChildren = true;
                        }
                    }
                })
            }
            this.categories = response.data;
            this.loadingCategories = false;
        });
    },
    computed : {
        advancedSearchIdentifierFields () {
            return Object.values(this.advancedSearchFields).filter(f => f.identifierType);
        },
        advancedSearchMiddleColumnFields () {
            return Object.values(this.advancedSearchFields).filter(f => f.middleColumn);
        },
        advancedSearchLastColumnFields () {
            return Object.values(this.advancedSearchFields).filter(f => f.lastColumn);
        },
        activeSortField () {
            return this.sortFields.find(sf => sf.fieldHandle === this.filters.sortColumnHandle && sf.desc === this.filters.sortDescending) || this.sortFields[0];
        },
        channelId() {
            return this.$getDealer().channelId;
        },
    },
    methods : {
        searchCacheApplier (savedContext) {
            if(savedContext) {
                this.categorySearch = savedContext;
            }
        },
        searchCacheAugmenter () {
            return this.categorySearch;
        },
        toggleChildren (cat) {
            this.$set(cat, 'showChildren', !cat.showChildren);
        },

        changeSort (sortField) {
            this.filters.sortColumnHandle = sortField.fieldHandle;
            this.filters.sortDescending = sortField.desc;
            this.fetchItems(true);
        },

        applyAdvancedSearch () {
            const add = (label, fieldHandle, comparatorHandle, value) => {
                const context = {
                    label : `${label} ${value}`,
                    isAdvancedSearchField : true,
                    condition : {
                        fieldHandle : fieldHandle,
                        comparatorHandle : comparatorHandle,
                        condition : { value : value },
                    },
                };
                this.addDynamicFilterWithoutFetch(context);
                return context.dynamicId;
            };
            Object.values(this.advancedSearchFields).forEach(field => {
                if(field.isRange) {
                    if(field.min.value) {
                        field.dynamicId = add(
                            field.label + ' >= ',
                            field.fieldHandle,
                            field.min.comparatorHandle,
                            field.min.value
                        );
                    }
                    if(field.max.value) {
                        field.dynamicId = add(
                            field.label + ' <= ',
                            field.fieldHandle,
                            field.max.comparatorHandle,
                            field.max.value
                        );
                    }
                }
                else if(field.value) {
                    field.dynamicId = add(
                        field.label + ' : ',
                        field.fieldHandle,
                        field.comparatorHandle,
                        field.value
                    );
                }
            });
            this.showAdvancedSearch = false;
            this.fetchItems(true);
        },

        clearAdvancedSearch () {
            Object.values(this.advancedSearchFields).forEach(field => {
                if(field.isRange) {
                    field.min.value = null;
                    field.min.max = null;
                } else {
                    field.value = null;
                }
                this.clearAdvancedSearchField(field);
            });
            this.showAdvancedSearch = false;
            this.fetchItems(true);
        },

        clearAdvancedSearchField (field) {
            this.appliedFilters = this.appliedFilters.filter(f => f.dynamicId !== field.dynamicId);
            this.filters.dynamicFilters = this.filters.dynamicFilters.filter(df => df.dynamicId !== field.dynamicId);
        },

        customRemoveDynamicFilter (context) {
            if(context.forCategory) {
                this.categorySearch = {};
            }
            else if(context.isAdvancedSearchField) {
                const advField = Object.values(this.advancedSearchFields).find(f => f.fieldHandle === context.condition.fieldHandle);
                if(advField){
                    if(advField.isRange) {
                        if(context.condition.comparatorHandle === 'greater_equal') {
                            advField.min.value = null;
                        } else {
                            advField.max.value = null;
                        }
                    } else {
                        advField.value = null;
                    }
                }
            }
            this.removeDynamicFilter(context);
        },

        removeCategorySearch () {
            const context = this.appliedFilters.find(f => f.label.indexOf('Category') !== -1);
            if(!context) {
                return;
            }
            this.appliedFilters = this.appliedFilters.filter(f => f.dynamicId !== context.dynamicId);
            this.filters.dynamicFilters.splice(context.dynamicId, 1);
        },

        searchCategory (cat) {

            if(this.categorySearch[cat.id] !== undefined) {
                this.removeCategorySearch();
                this.categorySearch = {};
                this.fetchItems(true);
                return;
            }

            this.removeCategorySearch();


            this.categorySearch = { [cat.id] : 'parent' };
            this.addDynamicFilter(
                {
                    label : `Category : ${cat.name}`,
                    condition : {
                        fieldHandle : 'product.categoryOneId',
                        comparatorHandle : 'equal',
                        condition : { value : cat.id },
                    },
                }
            );
            this.scrollToTop();
        },
        searchChildCategory (cat, child) {

            if(this.categorySearch[child.id] !== undefined) {
                this.removeCategorySearch();
                this.categorySearch = {};
                this.fetchItems(true);
                return;
            }

            this.removeCategorySearch();

            this.categorySearch = {
                [cat.id] : 'parent',
                [child.id] : 'child',
            };
            this.addDynamicFilter(
                {
                    label : `Category : ${cat.name} -> ${child.name}`,
                    forCategory : true,
                    condition : {
                        fieldHandle : 'product.categoryTwoId',
                        comparatorHandle : 'equal',
                        condition : { value : child.id },
                    },
                }
            );
            this.scrollToTop();
        },
        scrollToTop () {
            window.scrollTo({ top : this.$refs.top.getBoundingClientRect().top, behavior : 'smooth' });
        },
    },
}
</script>

<style lang="scss">
    @import '../../components/abstract-search/css/abstract-search';

    .download-con {
        border-bottom:1px solid $grey-lighter;
        margin-bottom:10px;
        padding-bottom:15px;
    }

    .search-con {
        margin-left:15px;
        position:relative;

        .advanced-search-con {
            position:absolute;
            width:100%;
            z-index:40;
            border:1px solid lighten($link, 48);
            border-radius:3px;
            margin-top:10px;
            overflow:hidden;
            background-color:$white;
            .advanced-search-header-or-footer {
                background-color:lighten($link, 58);
                padding:10px 20px;
            }
            .advanced-search-body {
                padding:20px;
            }
        }

    }

    .filter-con {
        background-color:$white-bis;
        padding:0 10px;
        margin:15px 0 0 15px;
        border-radius:3px;
        border:1px solid $grey-lighter;
    }

    .sidebar-col {
        flex: 0 0 200px;
        border-right: 1px solid $grey-lighter;
        padding-right: 12px;
    }

    .cat-col {
        & > .parent-cat {
            padding:4px 0;
            margin:4px 0;
            border-bottom:1px solid $grey-lighter;
            cursor:pointer;

            &:last-child {
                border-bottom:none;
            }

            &.selected .parent-name,
            .parent-name:hover {
                color:$link;
                font-weight:bold;
            }
            .toggle-con {
                font-size:16px;
                &:hover {
                    color: $link;
                    font-weight: bold;
                }
            }
        }

        .child-cats {
            border-top:none;
            border-radius:0 3px 3px 0;
            clear:both;
            width:100%;
            display:block;

            $borderColor:$grey-lighter;

            .border-wall {
                margin-left:5%;
                padding-left:8%;
                padding-top:10px;
                border-left:1px solid $borderColor;
                overflow:hidden;

                & > div {
                    position:relative;
                    height:100%;
                    display:block;
                }
            }

            .child-cat {
                width:100%;
                margin-bottom:4px;
                padding:4px;
                white-space:normal;
                text-align:left;
                cursor:pointer;
                border-bottom:2px solid inherit;
                font-size:12px;

                &:before {
                    content:'';
                    display:block;
                    position:absolute;
                    left:-10.5%;
                    border-top:1px solid $borderColor;
                    height:1px;
                    width:10.75%;
                    top:50%;
                }

                &.selected,
                &:hover {
                    font-weight:bold;
                    color:$link;
                    &:before {
                        top:49% !important;
                        border-top:2px solid $link;
                    }
                }


            }
        }


    }

</style>
